// ---------------------------------------------------------------------------
// useTaskDrafts — owns the single-run `taskDrafts` state (always one task,
// one agent reference) plus the mutators that touch it (agent reference,
// task name, grader rows).
//
// Bundling these here keeps the form file focused on render + submit. The
// hook is intentionally tied to the form's TaskDraft shape — it's not a
// general-purpose primitive.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

import type { AgentDraft, TaskDraft } from '../EvaluationTemplateForm.types.js';
import { makeTask, uid } from '../EvaluationTemplateForm.utils.js';

export interface UseTaskDraftsParams {
  initialDrafts?: TaskDraft[];
}

export interface UseTaskDraftsResult {
  taskDrafts: TaskDraft[];
  updateAgent: (
    taskId: string,
    agentId: string,
    patch: Partial<AgentDraft>,
  ) => void;
  updateTask: (taskId: string, patch: Partial<Pick<TaskDraft, 'name'>>) => void;
  addGraderRow: (taskId: string, name?: string) => void;
  removeGraderRow: (taskId: string, rowId: string) => void;
}

export function useTaskDrafts({
  initialDrafts,
}: UseTaskDraftsParams): UseTaskDraftsResult {
  const [taskDrafts, setTaskDrafts] = useState<TaskDraft[]>(
    () => initialDrafts ?? [makeTask('default')],
  );

  const updateAgent = useCallback(
    (taskId: string, agentId: string, patch: Partial<AgentDraft>) => {
      setTaskDrafts((rows) =>
        rows.map((t) =>
          t.id === taskId && t.agent.id === agentId
            ? { ...t, agent: { ...t.agent, ...patch } }
            : t,
        ),
      );
    },
    [],
  );

  const updateTask = useCallback(
    (taskId: string, patch: Partial<Pick<TaskDraft, 'name'>>) => {
      setTaskDrafts((rows) =>
        rows.map((t) => (t.id === taskId ? { ...t, ...patch } : t)),
      );
    },
    [],
  );

  const addGraderRow = useCallback((taskId: string, name = '') => {
    setTaskDrafts((rows) =>
      rows.map((t) =>
        t.id === taskId
          ? { ...t, graderRows: [...t.graderRows, { id: uid('g'), name }] }
          : t,
      ),
    );
  }, []);

  const removeGraderRow = useCallback((taskId: string, rowId: string) => {
    setTaskDrafts((rows) =>
      rows.map((t) =>
        t.id === taskId
          ? { ...t, graderRows: t.graderRows.filter((g) => g.id !== rowId) }
          : t,
      ),
    );
  }, []);

  return {
    taskDrafts,
    updateAgent,
    updateTask,
    addGraderRow,
    removeGraderRow,
  };
}
